import type { StoreProduct } from './store';

export type SourceLane = 'domestic' | 'china' | 'japan';
export type ProspectStage = 'public_screened' | 'discovery_listing_only';
export type ProspectDisposition = 'advance_to_authenticated_quote' | 'alternate_only';

export type SourceProspect = {
  targetSlotId: string;
  sourceLane: SourceLane;
  selectionMode: StoreProduct['selectionMode'];
  stage: ProspectStage;
  disposition: ProspectDisposition;
  publicListingRef: string;
  publicTitle: string;
  sourceUrl: string;
  publicPrice: { currency: 'KRW' | 'USD' | 'EUR' | 'JPY'; min: number; max: number; basis: string } | null;
  publicMoq: number | null;
  availabilityEvidence: string;
  checkedAt: '2026-08-24';
  fitCaveat: string;
  missingGates: string[];
};

const domesticGates = [
  'signed_in_wholesale_quote',
  'supplier_contract_and_sla',
  'source_sku_and_option_lock',
  'image_and_copy_rights',
  'sample_and_dimensional_qa',
  'korean_3pl_intake_and_return_contract',
  'landed_cost_and_margin_floor'
];

const importGates = [
  'authenticated_commercial_quote',
  'supplier_contract_and_sla',
  'source_sku_and_option_lock',
  'commercial_importer_and_customs_route',
  'hs_code_origin_and_korean_label_review',
  'sku_specific_kc_and_safety_review',
  'image_design_and_resale_rights',
  'sample_and_dimensional_qa',
  'korean_3pl_intake_and_return_contract',
  'landed_cost_and_margin_floor'
];

/**
 * 공개 웹에서 견적 진입 여부만 확인한 20개 prospect다.
 * supplier SKU, 판매 가능 상품, 재고 확정 또는 선정 완료를 뜻하지 않는다.
 */
export const initialSourceProspects: SourceProspect[] = [
  {
    targetSlotId: 'CA-SLOT-001', sourceLane: 'domestic', selectionMode: 'universal_utility', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'domeggook:66369358', publicTitle: '케이블 홀더 공개 상품', sourceUrl: 'https://www.domeggook.com/66369358?from=lstNewItem',
    publicPrice: { currency: 'KRW', min: 8800, max: 8800, basis: '공개 표시가·1개 기준, 계약 공급가 아님' }, publicMoq: 1,
    availabilityEvidence: '공개 상품 페이지와 최소 주문수량만 확인', checkedAt: '2026-08-24', fitCaveat: '현재 슬롯의 재사용 타이 구성과 동일한지는 로그인 견적과 실물로 확인해야 한다.', missingGates: [...domesticGates]
  },
  {
    targetSlotId: 'CA-SLOT-002', sourceLane: 'domestic', selectionMode: 'universal_utility', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'domeggook:61999367', publicTitle: '다구 케이블 클립 공개 상품', sourceUrl: 'https://domeggook.com/61999367?from=lstGen',
    publicPrice: { currency: 'KRW', min: 1050, max: 1050, basis: '공개 표시가·개당, 최소 5개' }, publicMoq: 5,
    availabilityEvidence: '공개 상품 페이지와 최소 주문수량만 확인', checkedAt: '2026-08-24', fitCaveat: '접착면 손상·케이블 구경·구성 수량을 실측하기 전에는 호환을 주장하지 않는다.', missingGates: [...domesticGates]
  },
  {
    targetSlotId: 'CA-SLOT-005', sourceLane: 'domestic', selectionMode: 'universal_utility', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'domeggook:67079883', publicTitle: '데스크 정리함 공개 상품', sourceUrl: 'https://domeggook.com/67079883?from=lstNewSeller',
    publicPrice: { currency: 'KRW', min: 10760, max: 10760, basis: '공개 표시가·1개 기준' }, publicMoq: 1,
    availabilityEvidence: '공개 상품 페이지와 공개 이미지 사용 표시를 확인', checkedAt: '2026-08-24', fitCaveat: '이미지 사용 표시는 채널별 광고·편집·재배포 권리 계약을 대신하지 않는다.', missingGates: [...domesticGates]
  },
  {
    targetSlotId: 'CA-SLOT-007', sourceLane: 'domestic', selectionMode: 'universal_utility', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'domeggook:60147072', publicTitle: '길이 조절 서랍 칸막이 공개 상품', sourceUrl: 'https://domeggook.com/60147072',
    publicPrice: { currency: 'KRW', min: 3420, max: 3490, basis: '공개 수량 구간가·최소 2개' }, publicMoq: 2,
    availabilityEvidence: '공개 페이지에 수량 구간가와 조회 시점 재고 표시가 존재', checkedAt: '2026-08-24', fitCaveat: '공개 재고는 계약 재고가 아니며 길이 범위·압착 흔적을 실측해야 한다.', missingGates: [...domesticGates]
  },
  {
    targetSlotId: 'CA-SLOT-008', sourceLane: 'domestic', selectionMode: 'universal_utility', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'domeggook:65996446', publicTitle: '회전형 정리 트레이 공개 상품', sourceUrl: 'https://domeggook.com/65996446',
    publicPrice: { currency: 'KRW', min: 10470, max: 10470, basis: '공개 표시가·1개 기준' }, publicMoq: 1,
    availabilityEvidence: '공개 상품 페이지와 최소 주문수량만 확인', checkedAt: '2026-08-24', fitCaveat: '모듈형 서랍 트레이 가설의 대체 후보이며 치수·서랍 적합성이 낮으면 탈락한다.', missingGates: [...domesticGates]
  },
  {
    targetSlotId: 'CA-SLOT-009', sourceLane: 'domestic', selectionMode: 'universal_utility', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'domeggook:64889607', publicTitle: '접이식 의류·비식품 수납 트레이 5개 공개 상품', sourceUrl: 'https://domeggook.com/64889607?from=lstNewItem',
    publicPrice: { currency: 'KRW', min: 8300, max: 8300, basis: '공개 표시가·5개 구성 기준' }, publicMoq: 1,
    availabilityEvidence: '공개 상품 페이지와 구성 표기만 확인', checkedAt: '2026-08-24', fitCaveat: '비식품 수납 용도로만 검토하며 봉제·냄새·하중을 샘플로 확인한다.', missingGates: [...domesticGates]
  },
  {
    targetSlotId: 'CA-SLOT-003', sourceLane: 'china', selectionMode: 'universal_utility', stage: 'public_screened', disposition: 'alternate_only',
    publicListingRef: 'alibaba:1601228232289', publicTitle: '접착식 가전 코드 정리 홀더 공개 상품', sourceUrl: 'https://www.alibaba.com/product-detail/Cord-Organizer-for-Kitchen-Wholesale-Adhesive_1601228232289.html',
    publicPrice: { currency: 'EUR', min: 0.3001, max: 0.3001, basis: '조회 세션 공개 표시가·운임/세금 제외' }, publicMoq: 10,
    availabilityEvidence: '공개 MOQ 10개와 sample 최대 5개 표시를 확인', checkedAt: '2026-08-24', fitCaveat: '비접착 가설과 다른 접착식이므로 슬롯 가설을 바꾸지 않는 한 대체 후보로만 둔다.', missingGates: [...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-004', sourceLane: 'china', selectionMode: 'universal_utility', stage: 'discovery_listing_only', disposition: 'alternate_only',
    publicListingRef: 'alibaba:showroom-cable-cord-sleeves', publicTitle: '지퍼형 PET 케이블 슬리브 공개 검색 오퍼', sourceUrl: 'https://www.alibaba.com/wholesale/cable-cord-sleeves.html',
    publicPrice: { currency: 'USD', min: 0.65, max: 1, basis: '공개 검색 오퍼 범위·운임/세금 제외' }, publicMoq: 10,
    availabilityEvidence: '공개 검색면의 가격·MOQ만 확인했고 exact 상세 SKU는 미고정', checkedAt: '2026-08-24', fitCaveat: '정확한 공급자·소재·길이·난연 주장을 고정할 수 없어 로그인 상세 견적 전에는 후보 승격 불가다.', missingGates: ['exact_product_detail', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-006', sourceLane: 'china', selectionMode: 'universal_utility', stage: 'public_screened', disposition: 'alternate_only',
    publicListingRef: 'alibaba:1600111591718', publicTitle: '실리콘 재사용 케이블 타이 공개 상품', sourceUrl: 'https://www.alibaba.com/product-detail/Eco-Friendly-Colorful-Magic-Cable-Tie_1600111591718.html',
    publicPrice: null, publicMoq: 50,
    availabilityEvidence: '공개 상품 페이지와 MOQ 50개만 확인', checkedAt: '2026-08-24', fitCaveat: '세로형 도구 홀더 가설과 일치하지 않아 다른 케이블 슬롯 대체가 필요할 때만 재배치 검토한다.', missingGates: ['slot_remap_decision', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-010', sourceLane: 'china', selectionMode: 'universal_utility', stage: 'public_screened', disposition: 'alternate_only',
    publicListingRef: 'alibaba:1601228465493', publicTitle: '25개 투명 서랍 정리함 공개 상품', sourceUrl: 'https://www.alibaba.com/product-detail/25-pcs-Clear-Plastic-Drawer-Organizers_1601228465493.html',
    publicPrice: { currency: 'USD', min: 10.19, max: 10.19, basis: '공개 표시가·세트, 운임/세금 제외' }, publicMoq: 10,
    availabilityEvidence: '공개 MOQ·표시가·공급자 공개 지표만 확인', checkedAt: '2026-08-24', fitCaveat: '선반 하부 바스켓 가설과 달라 트레이 슬롯 재배치 또는 탈락 판정이 필요하다.', missingGates: ['slot_remap_decision', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-011', sourceLane: 'china', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'alibaba:1600523866408', publicTitle: '3단 9칸 파스텔 데스크 정리함 공개 상품', sourceUrl: 'https://www.alibaba.com/product-introduction/3-Tier-Desktop-Organizer-Plastic-Storage_1600523866408.html',
    publicPrice: { currency: 'USD', min: 1.02, max: 1.02, basis: '공개 10~99개 구간 표시가·운임/세금 제외' }, publicMoq: 10,
    availabilityEvidence: '공개 introduction 페이지의 가격·구성만 확인', checkedAt: '2026-08-24', fitCaveat: '페이지의 AI 생성 소개는 사실 근거로 쓰지 않고 실물·공급자 원문만 상세페이지 근거로 쓴다.', missingGates: [...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-012', sourceLane: 'china', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'alternate_only',
    publicListingRef: 'alibaba:1600599338018', publicTitle: '투명 4칸 소형 수납함 공개 상품', sourceUrl: 'https://www.alibaba.com/product-detail/Plastic-Food-Storage-Organizer-Bins-Divided_1600599338018.html',
    publicPrice: { currency: 'USD', min: 2.29, max: 2.29, basis: '공개 10~99개 구간 표시가·운임/세금 제외' }, publicMoq: 10,
    availabilityEvidence: '공개 MOQ와 sample 표시를 확인', checkedAt: '2026-08-24', fitCaveat: '식품 접촉 적합성을 주장하지 않고 비식품 수납만 검토하며 라벨 모티프 가설과는 다르다.', missingGates: ['slot_remap_decision', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-019', sourceLane: 'china', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'alternate_only',
    publicListingRef: 'alibaba:1601267573241', publicTitle: '7-in-1 키보드 청소 키트 공개 상품', sourceUrl: 'https://www.alibaba.com/product-detail/7-In-1-Keyboard-Cleaning-Brush_1601267573241.html',
    publicPrice: { currency: 'USD', min: 4.8, max: 4.8, basis: '공개 1~49개 표시가·운임/세금 제외' }, publicMoq: 1,
    availabilityEvidence: '공개 상품·sample 가격 표시를 확인', checkedAt: '2026-08-24', fitCaveat: '분무 용기가 포함되어 액체 동봉 여부·누수·통관·표시 위험이 닫히지 않으면 탈락한다.', missingGates: ['liquid_container_and_contents_review', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-020', sourceLane: 'china', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'alibaba:1600740957481', publicTitle: '수납식 헤드폰·키보드 청소 펜 공개 상품', sourceUrl: 'https://www.alibaba.com/product-detail/New-Retractable-Headphone-Cleaning-Pen-Keyboard_1600740957481.html',
    publicPrice: { currency: 'USD', min: 1.8, max: 1.8, basis: '공개 10~99개 구간 표시가·운임/세금 제외' }, publicMoq: 10,
    availabilityEvidence: '공개 MOQ·sample 표시를 확인', checkedAt: '2026-08-24', fitCaveat: '비전기·무액체 구성과 자립 여부를 실물로 확인하고 국내 유사 디자인권을 별도 조사한다.', missingGates: [...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-013', sourceLane: 'japan', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'superdelivery:6534288', publicTitle: 'LIHIT LAB Bloomin 트레이 펜 케이스 A7731-5', sourceUrl: 'https://www.superdelivery.com/en/r/pd_p/6534288/',
    publicPrice: null, publicMoq: 1,
    availabilityEvidence: '공개 JAN 4903419830366·1개 단위·조회 시점 재고 표시 확인', checkedAt: '2026-08-24', fitCaveat: '로그인 도매가·한국 수출 가능 여부가 미확정이며 샘플은 QA 전용으로 분리한다.', missingGates: ['super_delivery_business_account', 'sample_not_for_resale_separation', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-014', sourceLane: 'japan', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'alternate_only',
    publicListingRef: 'superdelivery:13963080', publicTitle: 'KOKUYO Wammy Holder 펜 케이스 F-VBF340D', sourceUrl: 'https://www.superdelivery.com/en/r/pd_p/13963080/',
    publicPrice: null, publicMoq: 3,
    availabilityEvidence: '공개 JAN 4901480434933·3개 묶음·조회 시점 재고 표시 확인', checkedAt: '2026-08-24', fitCaveat: '키보드 부품 수납북 가설과 다르므로 실제 수납 호환이 증명되지 않으면 다른 파우치 슬롯으로 재배치한다.', missingGates: ['slot_remap_decision', 'super_delivery_business_account', 'sample_not_for_resale_separation', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-015', sourceLane: 'japan', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'advance_to_authenticated_quote',
    publicListingRef: 'superdelivery:12373495', publicTitle: 'KING JIM Pouzoo 물개 펜 파우치 PZC2050', sourceUrl: 'https://www.superdelivery.com/en/r/pd_p/12373495/',
    publicPrice: null, publicMoq: 1,
    availabilityEvidence: '공개 JAN 4971660782857·1개 단위·조회 시점 재고 표시 확인', checkedAt: '2026-08-24', fitCaveat: '아카이브 파우치 기능과 정확히 같지 않으므로 귀여움만으로 선정하지 않고 반복 사용 장면을 검증한다.', missingGates: ['super_delivery_business_account', 'sample_not_for_resale_separation', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-016', sourceLane: 'japan', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'alternate_only',
    publicListingRef: 'superdelivery:16345547', publicTitle: 'LIHIT LAB Kissa PuniLabo 미니 스탠드 펜 케이스 A8811-1', sourceUrl: 'https://www.superdelivery.com/p/r/pd_p/16345547/',
    publicPrice: null, publicMoq: 1,
    availabilityEvidence: '공개 품번·1개/10개 단위 표시 확인', checkedAt: '2026-08-24', fitCaveat: '한 손 잠금 클립 가설과 다르므로 파우치군 대체 후보일 뿐이며 독자 디자인의 정식 재판매권을 확인한다.', missingGates: ['slot_remap_decision', 'super_delivery_business_account', 'sample_not_for_resale_separation', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-017', sourceLane: 'japan', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'alternate_only',
    publicListingRef: 'midori:65478006', publicTitle: 'MIDORI Mini Cleaner II 한정 컬러', sourceUrl: 'https://www.midori-store.net/smp/item/65478006.html',
    publicPrice: { currency: 'JPY', min: 616, max: 616, basis: '공식 소매 표시가·도매가 아님' }, publicMoq: 1,
    availabilityEvidence: '공식 소매 페이지의 품번·크기·소매가만 확인', checkedAt: '2026-08-24', fitCaveat: '공식 소매점 구매품을 도매 공급으로 간주하지 않으며 수출·재판매 가능한 공인 도매원을 새로 확보해야 한다.', missingGates: ['authorized_export_wholesaler', 'wholesale_price_and_moq', ...importGates]
  },
  {
    targetSlotId: 'CA-SLOT-018', sourceLane: 'japan', selectionMode: 'specific_delight', stage: 'public_screened', disposition: 'alternate_only',
    publicListingRef: 'superdelivery:12773283', publicTitle: 'LIHIT LAB PuniLabo 스탠드 펜 케이스 펭귄 A7712-10', sourceUrl: 'https://www.superdelivery.com/en/r/pd_p/12773283/',
    publicPrice: null, publicMoq: 1,
    availabilityEvidence: '공개 JAN 4903419839628·1개 단위·조회 시점 재고 표시 확인', checkedAt: '2026-08-24', fitCaveat: '미니 빗자루 가설과 다르므로 자립 오브제 경험을 검증하는 대체 후보로만 둔다.', missingGates: ['slot_remap_decision', 'super_delivery_business_account', 'sample_not_for_resale_separation', ...importGates]
  }
];

const countBy = <T extends string>(values: T[]) => Object.fromEntries(values.map((value) => [value, initialSourceProspects.filter((item) => item.sourceLane === value).length])) as Record<T, number>;

export const sourceProspectSummary = {
  truth: 'public_screen_only' as const,
  checkedAt: '2026-08-24' as const,
  total: initialSourceProspects.length,
  byLane: countBy<SourceLane>(['domestic', 'china', 'japan']),
  utility: initialSourceProspects.filter((item) => item.selectionMode === 'universal_utility').length,
  delight: initialSourceProspects.filter((item) => item.selectionMode === 'specific_delight').length,
  advanceToAuthenticatedQuote: initialSourceProspects.filter((item) => item.disposition === 'advance_to_authenticated_quote').length,
  alternateOnly: initialSourceProspects.filter((item) => item.disposition === 'alternate_only').length,
  salesEnabled: 0,
  nextGate: 'authenticated_quote_contract_sample_import_rights_and_3pl'
};
